import { useMutation, useQuery } from '@apollo/client';
import type { ApolloError, ApolloQueryResult } from '@apollo/client';
import { useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import {
  GET_EDITABLE_DOCUMENT,
  DISCARD_EDITABLE_DOCUMENT_VERSION,
  CONFIRM_EDITABLE_DOCUMENT,
  CREATE_EDITABLE_DOCUMENT_IMAGE_ATTACHMENT,
  COMPLETE_EDITABLE_DOCUMENT_IMAGE_UPLOAD,
  CREATE_EDITABLE_DOCUMENT_HTML_PRESIGNED_UPLOAD,
  COMPLETE_EDITABLE_DOCUMENT_HTML_UPLOAD,
} from './TextEditorQueries';

/** Presigned upload needs a filename with a valid image extension (paste/drag may omit name). */
const uploadFilenameForFile = (file: File): string => {
  const trimmed = file.name?.trim();
  if (trimmed) {
    return trimmed;
  }
  const byType: Record<string, string> = {
    'image/jpeg': 'image.jpg',
    'image/jpg': 'image.jpg',
    'image/png': 'image.png',
    'image/gif': 'image.gif',
    'image/webp': 'image.webp',
  };
  return byType[file.type] ?? 'image.png';
};

export interface EditableDocumentQueryResult {
  data: ReturnType<typeof useQuery>['data'];
  loading: boolean;
  error?: ApolloError | undefined;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
}

interface UseEditableDocumentMutationsProps {
  documentId?: string;
  editorRef: React.RefObject<Editor | null>;
  // Tracks last loaded version to prevent double setContent calls
  lastLoadedVersionRef: React.MutableRefObject<number | null>;
  onContentUpdate?: (html: string, version: number) => void;
  onConfirm?: () => void;
  onDiscard?: () => void;
  /** When provided, use this query result instead of running GET_EDITABLE_DOCUMENT (avoids duplicate subscription). */
  documentQuery?: EditableDocumentQueryResult;
  /** Called after content is set from server (setContent); used to reset "user has edited" tracking. */
  onContentSetFromServer?: () => void;
}

export const useEditableDocumentMutations = ({
  documentId,
  editorRef,
  lastLoadedVersionRef,
  onContentUpdate,
  onConfirm,
  onDiscard,
  documentQuery: externalQuery,
  onContentSetFromServer,
}: UseEditableDocumentMutationsProps) => {
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [discardSuccess, setDiscardSuccess] = useState(false);
  const [discardError, setDiscardError] = useState<string | null>(null);
  const [confirmSuccess, setConfirmSuccess] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [revertSuccess, setRevertSuccess] = useState(false);
  const [revertError, setRevertError] = useState<string | null>(null);
  const [attachSuccess, setAttachSuccess] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);

  const internalQuery = useQuery(GET_EDITABLE_DOCUMENT, {
    variables: {
      editableDocumentVersionGroupId: documentId,
    },
    skip: !documentId || !!externalQuery,
  });

  const data = externalQuery?.data ?? internalQuery.data;
  const loading = externalQuery?.loading ?? internalQuery.loading;
  const error = externalQuery?.error ?? internalQuery.error;
  const refetch = externalQuery?.refetch ?? internalQuery.refetch;

  // Synchronous guard so a second save cannot start before the first completes (React
  // state `saving` updates async, so two calls in the same tick can both pass if (saving)).
  const saveInProgressRef = useRef(false);
  const documentIdRef = useRef<string | undefined>(documentId);
  if (documentIdRef.current !== documentId) {
    documentIdRef.current = documentId;
  }

  const [savePipelineBusy, setSavePipelineBusy] = useState(false);
  const [confirmPipelineBusy, setConfirmPipelineBusy] = useState(false);
  /** Set synchronously so autosave / interval callbacks see approve in progress before React re-renders. */
  const confirmPipelineInProgressRef = useRef(false);

  // Updates editor content and tracks version (prevents double setContent when refetch returns same version)
  const updateEditorContent = (html: string, version: number | null) => {
    if (html && editorRef.current) {
      if (version !== null) {
        lastLoadedVersionRef.current = version;
      }
      editorRef.current.commands.setContent(html);
      onContentSetFromServer?.();
      onContentUpdate?.(html, version ?? 0);
    }
  };

  const [createEditableDocumentHtmlPresignedUpload] = useMutation(CREATE_EDITABLE_DOCUMENT_HTML_PRESIGNED_UPLOAD);

  const [completeEditableDocumentHtmlUpload] = useMutation(COMPLETE_EDITABLE_DOCUMENT_HTML_UPLOAD, {
    refetchQueries: [GET_EDITABLE_DOCUMENT],
    awaitRefetchQueries: true,
  });

  const putHtmlToPresignedUrl = async (url: string, contentType: string, html: string) => {
    const res = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: html,
    });
    if (!res.ok) {
      throw new Error(`Storage upload failed (${res.status})`);
    }
  };

  type HtmlS3Result =
    | { ok: true; groupActiveVersionNumber: number | null }
    | { ok: false; stale: true }
    | { ok: false; error: string };

  const staleUploadMessages = [
    'document was updated elsewhere',
    'expected version',
    'current version is',
    'already confirmed',
    'cannot finalize html upload on a confirmed editable document',
    'invalid storage path for this editable document',
  ];

  const isStaleUploadError = (message: string): boolean => {
    const lower = message.toLowerCase();
    return staleUploadMessages.some((needle) => lower.includes(needle));
  };

  const messageFromUnknownError = (error: unknown, fallback: string): string => {
    if (error instanceof Error && error.message) {
      return error.message;
    }
    return fallback;
  };

  const runHtmlS3Upload = async (html: string, lockVersion: number): Promise<HtmlS3Result> => {
    if (!documentId) {
      return { ok: false, error: 'Missing document id' };
    }
    let presignResult;
    try {
      presignResult = await createEditableDocumentHtmlPresignedUpload({
        variables: {
          editableDocumentVersionGroupId: documentId,
          version: lockVersion,
        },
      });
    } catch (error) {
      const message = messageFromUnknownError(error, 'Failed to request HTML upload URL');
      if (isStaleUploadError(message)) {
        return { ok: false, stale: true };
      }
      return { ok: false, error: message };
    }
    const payload = presignResult.data?.createEditableDocumentHtmlPresignedUpload;
    if (!payload?.presignedPutUrl) {
      return { ok: false, stale: true };
    }
    const contentType = payload.contentType ?? 'text/html; charset=utf-8';
    try {
      await putHtmlToPresignedUrl(payload.presignedPutUrl, contentType, html);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to upload HTML to storage';
      return { ok: false, error: msg };
    }
    const ed = payload.editableDocument;
    if (!ed?.id || !payload.djangoFileName) {
      return { ok: false, error: 'Invalid presign response from server' };
    }
    let completeResult;
    try {
      completeResult = await completeEditableDocumentHtmlUpload({
        variables: {
          editableDocumentId: ed.id,
          djangoFileName: payload.djangoFileName,
        },
      });
    } catch (error) {
      const message = messageFromUnknownError(error, 'Failed to finalize document HTML upload');
      if (isStaleUploadError(message)) {
        return { ok: false, stale: true };
      }
      return { ok: false, error: message };
    }
    if (!completeResult.data?.completeEditableDocumentHtmlUpload?.success) {
      return { ok: false, error: 'Failed to finalize document HTML upload' };
    }
    const updated = completeResult.data.completeEditableDocumentHtmlUpload.editableDocument;
    return {
      ok: true,
      groupActiveVersionNumber: updated?.groupActiveVersionNumber ?? ed.groupActiveVersionNumber ?? null,
    };
  };

  const pendingRevertAfterDiscardRef = useRef<number | null>(null);

  const [discardEditableDocumentVersion, { loading: discarding }] = useMutation(DISCARD_EDITABLE_DOCUMENT_VERSION, {
    onCompleted: async (result) => {
      try {
        const pendingVersion = pendingRevertAfterDiscardRef.current;
        if (pendingVersion !== null) {
          pendingRevertAfterDiscardRef.current = null;
          return;
        }

        const discardedHtml = result?.discardEditableDocumentVersion?.editableDocument?.html;
        const discardedVersion = result?.discardEditableDocumentVersion?.editableDocument?.version;

        if (discardedHtml) {
          updateEditorContent(discardedHtml, discardedVersion ?? null);
        }

        await refetch({ editableDocumentVersionGroupId: documentId!, version: discardedVersion });

        setDiscardSuccess(true);
        setDiscardError(null);
        setTimeout(() => setDiscardSuccess(false), 3000);
        onDiscard?.();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
        setDiscardError(errorMessage);
        setDiscardSuccess(false);
      }
    },
    onError: (error) => {
      const errorMessage = error?.message || error?.graphQLErrors?.[0]?.message || 'Failed to discard version';
      setDiscardError(errorMessage);
      setDiscardSuccess(false);
    },
  });

  const [confirmEditableDocument, { loading: confirming }] = useMutation(CONFIRM_EDITABLE_DOCUMENT, {
    onCompleted: async () => {
      try {
        await refetch();
        setConfirmSuccess(true);
        setConfirmError(null);
        setTimeout(() => setConfirmSuccess(false), 3000);
        await Promise.resolve(onConfirm?.());
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
        setConfirmError(errorMessage);
        setConfirmSuccess(false);
      }
    },
    onError: (error) => {
      const errorMessage = error?.message || error?.graphQLErrors?.[0]?.message || 'Failed to confirm document';
      setConfirmError(errorMessage);
      setConfirmSuccess(false);
    },
  });

  const handleSave = async () => {
    if (!editorRef.current || !documentId || !data?.editableDocument) {
      return null;
    }
    if (confirmPipelineInProgressRef.current) {
      return null;
    }
    if (saveInProgressRef.current) {
      return null;
    }
    saveInProgressRef.current = true;
    setSavePipelineBusy(true);
    setSaveError(null);

    const html = editorRef.current.getHTML();
    const version = data.editableDocument.groupActiveVersionNumber ?? data.editableDocument.version;
    if (version == null) {
      saveInProgressRef.current = false;
      setSavePipelineBusy(false);
      return null;
    }

    try {
      const result = await runHtmlS3Upload(html, version);
      if (!result.ok) {
        if ('stale' in result) {
          await refetch();
          setSaveError('Document was updated elsewhere. Please try again.');
        } else {
          setSaveError(result.error);
        }
        setSaveSuccess(false);
        return null;
      }
      await refetch();
      setSaveSuccess(true);
      setSaveError(null);
      setTimeout(() => setSaveSuccess(false), 3000);
      return result.groupActiveVersionNumber;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save document';
      setSaveError(errorMessage);
      setSaveSuccess(false);
      return null;
    } finally {
      saveInProgressRef.current = false;
      setSavePipelineBusy(false);
    }
  };

  const handleDiscard = () => {
    if (!documentId || !data?.editableDocument) {
      return Promise.resolve();
    }

    return discardEditableDocumentVersion({
      variables: {
        editableDocumentVersionGroupId: documentId,
      },
    });
  };

  const waitForSavePipelineIdle = async (maxWaitMs: number) => {
    const start = Date.now();
    while (saveInProgressRef.current) {
      if (Date.now() - start > maxWaitMs) {
        return false;
      }
      await new Promise<void>((resolve) => {
        setTimeout(resolve, 50);
      });
    }
    return true;
  };

  const handleConfirm = async () => {
    if (!editorRef.current || !documentId || !data?.editableDocument) {
      return;
    }

    const html = editorRef.current.getHTML();
    const lockVersion = data.editableDocument.groupActiveVersionNumber ?? data.editableDocument.version;
    if (lockVersion == null) {
      return;
    }

    confirmPipelineInProgressRef.current = true;
    setConfirmPipelineBusy(true);
    setConfirmError(null);

    try {
      const saveIdle = await waitForSavePipelineIdle(90_000);
      if (!saveIdle) {
        setConfirmError('Save is still in progress. Wait for it to finish, then try again.');
        setConfirmSuccess(false);
        return;
      }
      const uploadResult = await runHtmlS3Upload(html, lockVersion);
      if (!uploadResult.ok) {
        if ('stale' in uploadResult) {
          await refetch();
          setConfirmError('Document was updated elsewhere. Refresh and try again.');
        } else {
          setConfirmError(uploadResult.error);
        }
        setConfirmSuccess(false);
        return;
      }

      const { data: fresh } = await refetch();
      const doc = fresh?.editableDocument;
      if (!doc) {
        setConfirmError('Could not reload document after upload.');
        setConfirmSuccess(false);
        return;
      }

      // Body is already in S3 (and linked via completeEditableDocumentHtmlUpload).
      // Omit html so confirm does not call set_body_html_in_storage() and strip html_file.
      await confirmEditableDocument({
        variables: {
          editableDocumentVersionGroupId: documentId,
          version: doc.version,
        },
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to confirm document';
      setConfirmError(errorMessage);
      setConfirmSuccess(false);
    } finally {
      confirmPipelineInProgressRef.current = false;
      setConfirmPipelineBusy(false);
    }
  };

  const handleDiscardThenRevertToVersion = (version: number) => {
    if (!documentId || !data?.editableDocument) {
      return;
    }
    pendingRevertAfterDiscardRef.current = version;
    discardEditableDocumentVersion({
      variables: {
        editableDocumentVersionGroupId: documentId,
      },
    });
  };

  const [createEditableDocumentImageAttachment, { loading: attaching }] = useMutation(
    CREATE_EDITABLE_DOCUMENT_IMAGE_ATTACHMENT,
    {
      onError: (error) => {
        setAttachError(error?.message || 'Failed to attach image');
        setAttachSuccess(false);
      },
    },
  );

  const [completeEditableDocumentImageUpload] = useMutation(COMPLETE_EDITABLE_DOCUMENT_IMAGE_UPLOAD);

  const handleAttachImage = async (
    file: File,
    onProgress?: (event: { progress: number }) => void,
    abortSignal?: AbortSignal,
  ): Promise<string> => {
    if (!documentId || !data?.editableDocument) {
      throw new Error('Image upload is not available — document is not loaded');
    }
    const filename = uploadFilenameForFile(file);
    try {
      setAttachError(null);
      const result = await createEditableDocumentImageAttachment({
        variables: {
          editableDocumentVersionGroupId: documentId,
          filename,
        },
      });
      const payload = result.data?.createEditableDocumentImageAttachment;
      const imageAttachment = payload?.imageAttachment;
      const presignedPostPayload = payload?.presignedPostPayload;
      const djangoFileName = payload?.djangoFileName;
      if (!imageAttachment?.id || !presignedPostPayload || !djangoFileName) {
        throw new Error('Failed to create image attachment');
      }
      await new Promise<void>((resolve, reject) => {
        let parsed: { url: string; fields: Record<string, string> };
        try {
          parsed = JSON.parse(presignedPostPayload) as {
            url: string;
            fields: Record<string, string>;
          };
        } catch {
          reject(new Error('Invalid presigned upload payload'));
          return;
        }
        const formData = new FormData();
        Object.entries(parsed.fields).forEach(([k, v]) => {
          formData.append(k, String(v));
        });
        formData.append('file', file);
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            onProgress?.({ progress: Math.round((e.loaded / e.total) * 100) });
          }
        });
        abortSignal?.addEventListener('abort', () => {
          xhr.abort();
          reject(new Error('Upload cancelled'));
        });
        xhr.addEventListener('load', () => {
          if ([200, 201, 204].includes(xhr.status)) {
            resolve();
          } else {
            reject(new Error('Failed to upload image to storage'));
          }
        });
        xhr.addEventListener('error', () => {
          reject(new Error('Failed to upload image to storage'));
        });
        xhr.open('POST', parsed.url);
        xhr.send(formData);
      });
      const completeResult = await completeEditableDocumentImageUpload({
        variables: {
          attachmentId: imageAttachment.id,
          djangoFileName,
        },
      });
      if (!completeResult.data?.completeEditableDocumentImageUpload?.success) {
        throw new Error('Failed to finalize image attachment');
      }
      setAttachSuccess(true);
      setTimeout(() => setAttachSuccess(false), 3000);
      return imageAttachment.permanentUrl;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to attach image';
      setAttachError(message);
      setAttachSuccess(false);
      throw error;
    }
  };

  const mutations = {
    save: {
      handle: handleSave,
      loading: savePipelineBusy,
      success: saveSuccess,
      error: saveError,
      setSuccess: setSaveSuccess,
      setError: setSaveError,
    },
    discard: {
      handle: handleDiscard,
      loading: discarding,
      success: discardSuccess,
      error: discardError,
      setSuccess: setDiscardSuccess,
      setError: setDiscardError,
    },
    confirm: {
      handle: handleConfirm,
      loading: confirming || confirmPipelineBusy,
      success: confirmSuccess,
      error: confirmError,
      setSuccess: setConfirmSuccess,
      setError: setConfirmError,
    },
    discardThenRevertToVersion: {
      handle: handleDiscardThenRevertToVersion,
      loading: discarding,
    },
    revert: {
      handle: () => handleDiscardThenRevertToVersion(1),
      loading: discarding,
      success: discardSuccess,
      error: discardError,
      setSuccess: setDiscardSuccess,
      setError: setDiscardError,
    },
    attachImage: {
      handle: handleAttachImage,
      loading: attaching,
      success: attachSuccess,
      error: attachError,
      setSuccess: setAttachSuccess,
      setError: setAttachError,
    },
  };

  return {
    data,
    loading,
    error,
    refetch,

    mutations,
    confirmPipelineInProgressRef,
  };
};

/** Mutation state shape for each document action (used by TextEditorActions) */
export type EditableDocumentMutations = ReturnType<typeof useEditableDocumentMutations>['mutations'];
